import type { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";

/** Percentage change between two periods; null when there is nothing to compare with. */
export const change = (current: number, previous: number) => (previous > 0 ? ((current - previous) / previous) * 100 : current > 0 ? null : 0);

/** Small bar chart for a statistics card (oldest to newest); the latest bar is highlighted. */
export function MiniBars({ values, dark }: { values: number[]; dark?: boolean }) {
  const max = Math.max(1, ...values);
  return (
    <div className="flex h-12 items-end gap-1" aria-hidden>
      {values.map((v, i) => (
        <span
          key={i}
          style={{ height: `${Math.max(8, (v / max) * 100)}%` }}
          className={`w-2 rounded-sm ${i === values.length - 1 ? (dark ? "bg-primary" : "bg-primary") : dark ? "bg-white/25" : "bg-gray-200"}`}
        />
      ))}
    </div>
  );
}

/**
 * A statistics card with its own graph: headline value, change against the previous period and
 * an 8-week bar chart. `dark` gives the highlighted first card of a row.
 */
export default function KpiCard({ label, value, unit, delta, deltaLabel = "vs previous period", trend, dark, footer, showTrend = true }: {
  label: string; value: ReactNode; unit?: string; delta?: number | null; deltaLabel?: string; trend: number[]; dark?: boolean; footer?: ReactNode; showTrend?: boolean;
}) {
  const up = delta !== null && delta !== undefined && delta >= 0;
  return (
    <div className={`flex flex-col justify-between rounded-2xl p-5 shadow-sm ${dark ? "bg-gray-900 text-white" : "border border-gray-200 bg-white"}`}>
      <p className={`text-xs ${dark ? "text-white/60" : "text-gray-500"}`}>{label}</p>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div className={`min-w-0 ${showTrend ? "" : "w-full"}`}>
          <p className={`${showTrend ? "truncate text-3xl" : "break-words text-2xl [overflow-wrap:anywhere]"} font-semibold tracking-tight`}>{value}{unit && <span className={`ml-1 text-base font-normal ${dark ? "text-white/60" : "text-gray-400"}`}>{unit}</span>}</p>
          <p className={`mt-1 flex items-center gap-1 text-[11px] ${dark ? "text-white/60" : "text-gray-500"}`}>
            {delta !== null && delta !== undefined && (
              <span className={`inline-flex items-center font-medium ${up ? "text-green-500" : "text-red-500"}`}>
                {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{Math.abs(delta).toFixed(1)}%
              </span>
            )}
            <span className="truncate">{deltaLabel}</span>
          </p>
        </div>
        {showTrend && <MiniBars values={trend} dark={dark} />}
      </div>
      {footer && <div className={`mt-3 border-t pt-3 text-[11px] ${dark ? "border-white/10 text-white/60" : "border-gray-100 text-gray-500"}`}>{footer}</div>}
    </div>
  );
}

/** Tiny trend line for table rows. */
export function Sparkline({ values, width = 96, height = 28 }: { values: number[]; width?: number; height?: number }) {
  const max = Math.max(1, ...values);
  const step = values.length > 1 ? width / (values.length - 1) : width;
  const points = values.map((v, i) => `${(i * step).toFixed(1)},${(height - 2 - (v / max) * (height - 4)).toFixed(1)}`).join(" ");
  const flat = values.every((v) => v === values[0]);
  const rising = values[values.length - 1] >= values[0];
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden>
      <polyline points={points} fill="none" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={flat ? "stroke-gray-300" : rising ? "stroke-green-500" : "stroke-red-400"} />
    </svg>
  );
}
